/**
 * Turn AI field suggestions into real marked variables — the gatekeeper between a language
 * model and the document.
 *
 * WHY THIS EXISTS. A `MarkedVariable` is only placeable if `selectedText` is an exact
 * (whitespace-normalized) substring of the flattened document AND its occurrence ordinal was
 * computed against the same text model the tokenizer uses. `tokenizeDocx` is all-or-nothing,
 * so a single unplaceable field blocks the entire save. An LLM cannot meet that bar: it
 * straightens curly quotes, normalizes dashes, silently fixes typos and drops odd spacing.
 *
 * So the agent is never asked for positional data. It supplies semantics — which values vary,
 * what to call them, what type they are, whose they are — plus the index of the paragraph it
 * read each value from. THIS module derives every span, context string and occurrence ordinal
 * itself, from `model`, and discards anything it cannot anchor. Because `model` is the
 * tokenizer's own `buildTextModel` output, every surviving suggestion is guaranteed to tokenize.
 *
 * Two consequences worth stating plainly:
 *  - Recall is traded for correctness. A real field the agent described inexactly is dropped
 *    silently rather than shown broken.
 *  - Mirrors are NOT asked of the agent. Once a value is anchored, finding its other
 *    occurrences is a string search this module cannot get wrong.
 */

// `TextModel` is imported as a TYPE ONLY: tokenize.ts pulls in pizzip + @xmldom/xmldom, and a
// value import would drag the whole XML stack into the marking bundle. The functions come from
// text-locate.ts, which is dependency-free for exactly this reason.
import { normalize, occurrenceAt } from '@/lib/docx/text-locate';
import type { TextModel } from '@/lib/docx/tokenize';
import type {
  DocxFieldSuggestions,
  SuggestedSpan,
} from '@/modules/templates/services/ai/docx-field-suggestions.types';
import {
  childName,
  companyChildName,
  toIdentifier,
  uniqueEntityKey,
  uniqueName,
} from './body-model';
import {
  COMPANY_SUB_LABELS,
  COMPANY_SUB_TYPE,
  SUB_LABELS,
  SUB_TYPE,
  type CompanyMeta,
  type MarkedVariable,
  type PersonMeta,
} from './types';

/** Chars of surrounding text stored with a span — matches DocxCanvas's manual capture. */
const CONTEXT_CHARS = 40;

/** How far either side of the cited paragraph to look before giving up. */
const PARAGRAPH_SEARCH_RADIUS = 2;

// ---------------------------------------------------------------------------
// Paragraph model
// ---------------------------------------------------------------------------

export interface DocumentParagraph {
  /** The model's own `paragraphIndex`, used verbatim as the `[n]` prefix in the prompt. */
  index: number;
  /** Raw slice of `model.V` covering this paragraph. */
  text: string;
  vStart: number;
  vEnd: number;
}

/**
 * Group the flattened document into paragraphs — both the prompt's numbered lines and the
 * search window each suggestion is resolved inside.
 *
 * Scoping the search to one cited paragraph is what makes anchoring reliable: a phrase that
 * repeats a dozen times document-wide is usually unique within its own paragraph, so the match
 * is unambiguous and the surrounding-context scoring never has to break a tie.
 */
export function documentParagraphs(model: TextModel): DocumentParagraph[] {
  const bounds = new Map<number, { vStart: number; vEnd: number }>();
  for (const seg of model.segments) {
    const end = seg.vStart + seg.text.length;
    const cur = bounds.get(seg.paragraphIndex);
    if (!cur) {
      bounds.set(seg.paragraphIndex, { vStart: seg.vStart, vEnd: end });
      continue;
    }
    if (seg.vStart < cur.vStart) cur.vStart = seg.vStart;
    if (end > cur.vEnd) cur.vEnd = end;
  }

  return [...bounds.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, span]) => ({
      index,
      text: model.V.slice(span.vStart, span.vEnd),
      vStart: span.vStart,
      vEnd: span.vEnd,
    }))
    .filter((p) => p.text.trim().length > 0);
}

// ---------------------------------------------------------------------------
// Text repair
// ---------------------------------------------------------------------------

/**
 * Length-PRESERVING character folds for the characters a model rewrites without being asked:
 * smart quotes, the dash family, and exotic spaces. Every replacement is 1:1 so an index into
 * the folded string is still valid in the original — which is what lets a repaired match map
 * back to real document offsets.
 */
const FOLDS: ReadonlyArray<readonly [RegExp, string]> = [
  // Single quotes, apostrophes, primes.
  [/[\u2018\u2019\u201A\u201B\u2032\u00B4\u0060]/g, "'"],
  // Double quotes and double primes.
  [/[\u201C\u201D\u201E\u201F\u2033]/g, '"'],
  // The dash family, including the minus sign.
  [/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212\u2043]/g, '-'],
  // NBSP, figure/narrow/thin/hair spaces, ideographic space, zero-width space. Mapping
  // zero-width to a plain space keeps every fold 1:1 in length -- which the offset
  // mapping depends on -- and is harmless, since normalize() collapses runs anyway.
  [/[\u00A0\u2007\u202F\u2009\u200A\u3000\u200B]/g, ' '],
];

function fold(s: string): string {
  return FOLDS.reduce((acc, [re, to]) => acc.replace(re, to), s);
}

/** A needle that is nothing but a blank fill-in line — "______", "....", "----". */
const BLANK_NEEDLE = /^[_\-.]{2,}$/;
/** The same, as it appears inside a paragraph. */
const BLANK_IN_TEXT = /_{2,}|\.{4,}|-{3,}/g;

type Offsets = { start: number; end: number };

/** Every offset of `needle` within `slice`, relative to `slice`. */
function occurrencesInSlice(slice: string, needle: string): Offsets[] {
  const { norm, map } = normalize(slice);
  const n = normalize(needle.trim()).norm;
  if (!n) return [];
  const out: Offsets[] = [];
  let idx = norm.indexOf(n);
  while (idx !== -1) {
    out.push({ start: map[idx], end: map[idx + n.length - 1] + 1 });
    idx = norm.indexOf(n, idx + 1);
  }
  return out;
}

/**
 * Candidate offsets for a suggested span within one paragraph, best first.
 *
 * Blank fill-in runs are handled FIRST and matched WHOLE. A model asked to copy "________"
 * reliably returns a different number of underscores, and a shorter run is a substring of a
 * longer one — so a plain search would anchor five of eight underscores, tokenize part of the
 * blank, and leave the rest stranded in the generated document. Only a complete run counts.
 *
 * Otherwise: verbatim, then character-folded for the typography a model rewrites. Several
 * candidates may come back (a paragraph can repeat a value, or hold two blanks); the caller
 * picks the first one not already claimed by another field.
 */
function findCandidates(slice: string, needle: string): Offsets[] {
  const folded = fold(needle).trim();

  if (BLANK_NEEDLE.test(folded)) {
    const out: Offsets[] = [];
    for (const m of slice.matchAll(BLANK_IN_TEXT)) {
      out.push({ start: m.index, end: m.index + m[0].length });
    }
    return out;
  }

  const exact = occurrencesInSlice(slice, needle);
  if (exact.length > 0) return exact;
  return occurrencesInSlice(fold(slice), folded);
}

// ---------------------------------------------------------------------------
// Anchored spans
// ---------------------------------------------------------------------------

/** Everything a `MarkedVariable` needs to be located in the document, derived not supplied. */
export interface AnchoredSpan {
  selectedText: string;
  contextBefore: string;
  contextAfter: string;
  paragraphIndex: number | null;
  occurrenceIndex: number;
  occurrenceCount: number;
  /** Raw bounds in `model.V` — used to reject overlaps, not persisted. */
  rawStart: number;
  rawEnd: number;
}

/** Which `w:p` a raw offset falls in, mirroring how the tokenizer records it. */
function paragraphAt(model: TextModel, rawStart: number): number | null {
  const seg = model.segments.find(
    (s) => s.node && s.vStart <= rawStart && s.vStart + s.text.length > rawStart
  );
  return seg ? seg.paragraphIndex : null;
}

/** Build an anchored span from real document offsets. */
function spanFromOffsets(model: TextModel, rawStart: number, rawEnd: number): AnchoredSpan | null {
  const selectedText = model.V.slice(rawStart, rawEnd).replace(/\s+/g, ' ').trim();
  if (!selectedText) return null;

  // occurrenceAt runs over the WHOLE document, so the ordinal is global — which is exactly
  // what locate() compares against at save time.
  const occurrence = occurrenceAt(model.V, selectedText, rawStart);
  if (!occurrence) return null;

  return {
    selectedText,
    contextBefore: model.V.slice(Math.max(0, rawStart - CONTEXT_CHARS), rawStart),
    contextAfter: model.V.slice(rawEnd, rawEnd + CONTEXT_CHARS),
    paragraphIndex: paragraphAt(model, rawStart),
    occurrenceIndex: occurrence.index,
    occurrenceCount: occurrence.count,
    rawStart,
    rawEnd,
  };
}

/**
 * Resolve one suggested span, or return null to drop it.
 *
 * The cited paragraph is a hint, not gospel — XML and rendered paragraph numbering can differ
 * slightly — so its neighbours are tried before giving up.
 *
 * `accept` lets the caller veto a candidate that is already spoken for. That is what makes a
 * signature block like "Name: ______  Date: ______" work: the first field takes the first
 * blank, and the second field, finding it claimed, moves on to the next.
 */
export function anchorSpan(
  model: TextModel,
  paragraphs: DocumentParagraph[],
  cited: number,
  text: string,
  accept: (span: AnchoredSpan) => boolean = () => true
): AnchoredSpan | null {
  if (!text?.trim()) return null;

  const byIndex = new Map(paragraphs.map((p) => [p.index, p]));
  const order: number[] = [cited];
  for (let d = 1; d <= PARAGRAPH_SEARCH_RADIUS; d++) order.push(cited - d, cited + d);

  for (const index of order) {
    const paragraph = byIndex.get(index);
    if (!paragraph) continue;
    for (const hit of findCandidates(paragraph.text, text)) {
      const span = spanFromOffsets(
        model,
        paragraph.vStart + hit.start,
        paragraph.vStart + hit.end
      );
      if (span && accept(span)) return span;
    }
  }
  return null;
}

/** Every raw span of `needle` in the document, in order — index i is occurrence i. */
function allOccurrenceSpans(V: string, needle: string): Array<{ start: number; end: number }> {
  const { norm, map } = normalize(V);
  const n = normalize(needle.trim()).norm;
  if (!n) return [];
  const out: Array<{ start: number; end: number }> = [];
  let idx = norm.indexOf(n);
  while (idx !== -1) {
    out.push({ start: map[idx], end: map[idx + n.length - 1] + 1 });
    idx = norm.indexOf(n, idx + 1);
  }
  return out;
}

/**
 * Every OTHER occurrence of an already-anchored value.
 *
 * This is why the agent is never asked to enumerate repeats: a party name appearing five times
 * needs five marked spans for the merge to fill them all, and finding them is a string search
 * with no failure mode. Asking a model to list them invites both misses and inventions.
 */
export function deriveMirrors(model: TextModel, source: AnchoredSpan): AnchoredSpan[] {
  const spans = allOccurrenceSpans(model.V, source.selectedText);
  if (spans.length < 2) return [];

  const out: AnchoredSpan[] = [];
  spans.forEach((s, index) => {
    if (index === source.occurrenceIndex) return;
    const span = spanFromOffsets(model, s.start, s.end);
    if (span) out.push(span);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

export interface AnchorResult {
  variables: MarkedVariable[];
  persons: Record<string, PersonMeta>;
  companies: Record<string, CompanyMeta>;
  /** Suggestions the document could not confirm. Reported so callers never imply full coverage. */
  dropped: number;
  /** Extra spans created for repeated values. */
  mirrors: number;
}

/** Accumulates accepted spans so nothing overlaps — the tokenizer rejects overlapping spans. */
class SpanClaims {
  private claimed: Array<{ start: number; end: number }> = [];

  free(span: AnchoredSpan): boolean {
    return !this.claimed.some((c) => span.rawStart < c.end && c.start < span.rawEnd);
  }

  take(span: AnchoredSpan): void {
    this.claimed.push({ start: span.rawStart, end: span.rawEnd });
  }
}

/** The span fields of a MarkedVariable, without the bookkeeping offsets. */
function spanFields(span: AnchoredSpan) {
  return {
    selectedText: span.selectedText,
    contextBefore: span.contextBefore,
    contextAfter: span.contextAfter,
    paragraphIndex: span.paragraphIndex,
    occurrenceIndex: span.occurrenceIndex,
    occurrenceCount: span.occurrenceCount,
  };
}

/**
 * Convert normalized suggestions into marking state.
 *
 * Runs against `existing` so identifier allocation never collides with work already present.
 * (In practice the trigger only fires on an empty panel, but the allocators are threaded
 * through the accumulating arrays anyway so this stays correct if that gating is relaxed.)
 */
export function anchorSuggestions(
  model: TextModel,
  suggestions: DocxFieldSuggestions,
  existing: {
    variables: MarkedVariable[];
    persons: Record<string, PersonMeta>;
    companies: Record<string, CompanyMeta>;
  }
): AnchorResult {
  const paragraphs = documentParagraphs(model);
  const variables: MarkedVariable[] = [...existing.variables];
  const persons: Record<string, PersonMeta> = { ...existing.persons };
  const companies: Record<string, CompanyMeta> = { ...existing.companies };

  const claims = new SpanClaims();
  let dropped = 0;
  let mirrors = 0;

  const entityKeys = () => [...Object.keys(persons), ...Object.keys(companies)];

  /**
   * Anchor a span and claim it, or count it as dropped. The claim check is passed INTO the
   * search so an already-taken candidate makes it try the next one rather than fail outright.
   */
  const claim = (s: SuggestedSpan): AnchoredSpan | null => {
    const span = anchorSpan(model, paragraphs, s.paragraphIndex, s.text, (c) => claims.free(c));
    if (!span) {
      dropped++;
      return null;
    }
    claims.take(span);
    return span;
  };

  /** Mirror a source value into its other occurrences, as plain copy fields. */
  const addMirrors = (source: AnchoredSpan, sourceName: string, sourceLabel: string) => {
    for (const span of deriveMirrors(model, source)) {
      if (!claims.free(span)) continue;
      claims.take(span);
      const name = uniqueName(variables, toIdentifier(sourceLabel), undefined, entityKeys());
      variables.push({
        name,
        label: `${sourceLabel} (copy)`,
        type: 'text',
        required: false,
        options: [],
        valueCopyFrom: sourceName,
        origin: 'ai',
        ...spanFields(span),
      });
      mirrors++;
    }
  };

  // ── People ────────────────────────────────────────────────────────────────
  // Entities come first so parties read at the top of the panel, the way a contract does.
  for (const person of suggestions.people) {
    const anchored = person.subFields
      .map((sub) => ({ sub, span: claim(sub) }))
      .filter((entry): entry is { sub: typeof entry.sub; span: AnchoredSpan } => !!entry.span);

    // A person without a Name cannot be saved (`save()` refuses), so drop the whole entity
    // rather than create something the user must repair before they can continue.
    if (!anchored.some((a) => a.sub.slot === 'name')) {
      dropped += anchored.length;
      continue;
    }

    const key = uniqueEntityKey(variables, entityKeys(), toIdentifier(person.label));
    persons[key] = {
      key,
      label: person.label,
      isSignatory: person.isSignatory,
      signingOrder: person.isSignatory ? person.signingOrder ?? 1 : null,
      companyRef: null,
      companyLabel: null,
      defaultSigner: null,
    };

    for (const { sub, span } of anchored) {
      const name = childName(key, sub.slot);
      const label = `${person.label} — ${SUB_LABELS[sub.slot]}`;
      variables.push({
        name,
        label,
        type: SUB_TYPE[sub.slot],
        required: sub.slot === 'name',
        options: [],
        personKey: key,
        subKind: sub.slot,
        origin: 'ai',
        ...spanFields(span),
      });
      // A signature is a location anchor, never a value — mirroring it would be meaningless.
      if (sub.mirrorRepeats && sub.slot !== 'signature') addMirrors(span, name, label);
    }
  }

  // ── Companies ─────────────────────────────────────────────────────────────
  for (const company of suggestions.companies) {
    const anchored = company.subFields
      .map((sub) => ({ sub, span: claim(sub) }))
      .filter((entry): entry is { sub: typeof entry.sub; span: AnchoredSpan } => !!entry.span);

    if (!anchored.some((a) => a.sub.slot === 'name')) {
      dropped += anchored.length;
      continue;
    }

    const key = uniqueEntityKey(variables, entityKeys(), toIdentifier(company.label));
    companies[key] = { key, label: company.label };

    for (const { sub, span } of anchored) {
      const name = companyChildName(key, sub.slot);
      const label = `${company.label} — ${COMPANY_SUB_LABELS[sub.slot]}`;
      variables.push({
        name,
        label,
        type: COMPANY_SUB_TYPE[sub.slot],
        required: sub.slot === 'name',
        options: [],
        companyKey: key,
        companySubKind: sub.slot,
        origin: 'ai',
        ...spanFields(span),
      });
      if (sub.mirrorRepeats) addMirrors(span, name, label);
    }
  }

  // ── Plain fields ──────────────────────────────────────────────────────────
  for (const field of suggestions.fields) {
    const span = claim(field);
    if (!span) continue;

    const name = uniqueName(variables, toIdentifier(field.label), undefined, entityKeys());
    variables.push({
      name,
      label: field.label,
      type: field.type,
      required: field.required,
      options: field.type === 'select' ? field.options ?? [] : [],
      placeholder: field.placeholder,
      helpText: field.helpText,
      currencyCode: field.type === 'currency' ? field.currencyCode : undefined,
      origin: 'ai',
      ...spanFields(span),
    });
    if (field.mirrorRepeats) addMirrors(span, name, field.label);
  }

  return { variables, persons, companies, dropped, mirrors };
}
